import {StyleSheet} from "aphrodite";
import * as React from "react";

import {addStyle, View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";

const StyledDiv = addStyle("div");

const WAVE_COUNT = 3;
const WAVE_HEIGHT = 12;
const WAVE_WIDTH = 2;

interface Props {
    /**
     * Whether the controls style should be disabled.
     */
    disabled?: boolean;
    /**
     * Optional styles to apply to the sound waves container.
     */
    style?: StyleType;
}

/**
 * Renders a set of sound waves.
 *
 * Used in the headphone and microphone buttons.
 */
export const SoundWaves = (props: Props): React.ReactElement => {
    const {disabled = false, style} = props;
    const bgColor = disabled
        ? semanticColor.core.foreground.instructive.subtle
        : semanticColor.core.foreground.instructive.default;

    const waves = Array.from({length: WAVE_COUNT}).map((_, i) => (
        <StyledDiv
            key={`sound-wave-${i}`}
            style={[
                styles.soundWave,
                {
                    animationName: soundWaveKeyFrames(i),
                    backgroundColor: bgColor,
                    width: WAVE_WIDTH,
                },
            ]}
        />
    ));
    return (
        <View style={[styles.wrapper, {gap: WAVE_WIDTH}, style]}>{waves}</View>
    );
};

const soundWaveKeyFrames = (offset: number): any => ({
    "0%": {
        height: `${(50 + offset * 50) % 120}%`,
    },
    "25%": {
        height: `${(100 + offset * 50) % 120}%`,
    },
    "50%": {
        height: `${(50 + offset * 50) % 120}%`,
    },
    "75%": {
        height: `${(0 + offset * 50) % 120}%`,
    },
    "100%": {
        height: `${(50 + offset * 50) % 120}%`,
    },
});

const styles = StyleSheet.create({
    wrapper: {
        flexDirection: "row",
        flex: "0 0 auto",
        justifyContent: "center",
        alignItems: "center",
        height: WAVE_HEIGHT,
        gap: WAVE_WIDTH,
    },
    soundWave: {
        borderRadius: WAVE_WIDTH / 2,
        animationDuration: "1000ms",
        animationDirection: "alternate",
        animationIterationCount: "infinite",
    },
});
